/**
 * Boleta o factura, y los datos tributarios que la factura exige (CHK-04).
 *
 * Los campos de empresa aparecen solo al elegir factura. No es solo comodidad:
 * la mayoria compra con boleta, y campos de empresa siempre visibles hacen
 * dudar a quien no tiene ninguno. Con factura va tambien la direccion
 * tributaria (`CamposDireccionFacturacion`).
 *
 * El RUT personal de la boleta no se pide aca sino en `PanelContacto`, junto al
 * nombre: abajo nadie lo veia. Es un campo aparte del RUT de la empresa, y no
 * el mismo: si fuera uno solo, pasar de boleta a factura dejaria el RUT
 * personal escrito como si fuera el de la empresa, y es facil no notarlo.
 *
 * El RUT se valida con su digito verificador en el navegador. Random rechaza un
 * RUT invalido con un error de datos, y ese rechazo llegaria despues de cobrar;
 * el modulo 11 atrapa el tipeo antes de que eso pase.
 *
 * El sitio NUNCA emite el documento: lo emite Random. Lo que se elige aca es
 * cual se le pide.
 */
import type { OpcionesDespacho } from '@elcarpintero/compartido';

import type { DatosCheckout, ErroresCheckout } from '../../utilidades/checkout';
import { rutAlEscribir } from '../../utilidades/rut';
import { Campo } from './Campo';
import { CamposDireccionFacturacion } from './CamposDireccionFacturacion';

interface Props {
  datos: DatosCheckout;
  errores: ErroresCheckout;
  opciones: OpcionesDespacho | null;
  alCambiar: (cambio: Partial<DatosCheckout>) => void;
  alSalirDelCampo: (campo: keyof DatosCheckout) => void;
}

export function PanelDocumento({ datos, errores, opciones, alCambiar, alSalirDelCampo }: Props) {
  return (
    <section className="panel" aria-labelledby="ck-documento">
      <div className="panel-cab">
        <h2 id="ck-documento">Documento</h2>
      </div>

      <div className="panel-cuerpo">
        <fieldset>
          <legend className="solo-lectores">Qué documento necesitas</legend>

          <label className={`opcion-envio${datos.tipoDocumento === 'boleta' ? ' sel' : ''}`}>
            <input
              type="radio"
              name="documento"
              value="boleta"
              checked={datos.tipoDocumento === 'boleta'}
              onChange={() => alCambiar({ tipoDocumento: 'boleta' })}
            />
            <span className="opcion-texto">
              <span className="t">Boleta electrónica</span>
              <span className="d">Para compras personales. Solo te pedimos tu RUT, y es opcional.</span>
            </span>
          </label>

          <label className={`opcion-envio${datos.tipoDocumento === 'factura' ? ' sel' : ''}`}>
            <input
              type="radio"
              name="documento"
              value="factura"
              checked={datos.tipoDocumento === 'factura'}
              onChange={() => alCambiar({ tipoDocumento: 'factura' })}
            />
            <span className="opcion-texto">
              <span className="t">Factura electrónica</span>
              <span className="d">
                Para empresas con giro. Necesitamos RUT, razón social, giro y dirección.
              </span>
            </span>
          </label>
        </fieldset>

        {datos.tipoDocumento === 'factura' ? (
          <div className="detalle-entrega">
            <div className="fila-campos">
              <Campo id="ck-rut" etiqueta="RUT de la empresa" error={errores.rut}>
                {(atributos) => (
                  <input
                    {...atributos}
                    type="text"
                    inputMode="text"
                    placeholder="12.345.678-5"
                    value={datos.rut}
                    maxLength={14}
                    autoComplete="off"
                    // Puntos y guion mientras se escribe, salvo que los ponga
                    // quien escribe (`rutAlEscribir`).
                    onChange={(evento) => alCambiar({ rut: rutAlEscribir(datos.rut, evento.target.value) })}
                    onBlur={() => alSalirDelCampo('rut')}
                  />
                )}
              </Campo>

              <Campo id="ck-razon" etiqueta="Razón social" error={errores.razonSocial}>
                {(atributos) => (
                  <input
                    {...atributos}
                    type="text"
                    autoComplete="organization"
                    value={datos.razonSocial}
                    onChange={(evento) => alCambiar({ razonSocial: evento.target.value })}
                    onBlur={() => alSalirDelCampo('razonSocial')}
                  />
                )}
              </Campo>
            </div>

            <Campo
              id="ck-giro"
              etiqueta="Giro"
              error={errores.giro}
              ayuda="La actividad económica con la que la empresa está inscrita en el SII."
            >
              {(atributos) => (
                <input
                  {...atributos}
                  type="text"
                  value={datos.giro}
                  onChange={(evento) => alCambiar({ giro: evento.target.value })}
                  onBlur={() => alSalirDelCampo('giro')}
                />
              )}
            </Campo>

            <CamposDireccionFacturacion
              datos={datos}
              errores={errores}
              opciones={opciones}
              alCambiar={alCambiar}
              alSalirDelCampo={alSalirDelCampo}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
}
